import TextBlock from '@components/text-block';
import useTwoColumn from '@components/two-column/two-column.hook';
import type { Theme } from 'constants/colors';
import type { ReactElement, ReactNode } from 'react';
import React, { useMemo } from 'react';
import type { TextBlockSize } from 'types/components/text-block';
import type { Layout } from 'types/layout';

interface Props {
  readonly description?: ReactNode | undefined;
  readonly eyebrow?: ReactNode | undefined;
  readonly heading: ReactNode;
  readonly layout?: Exclude<Layout, 'center'> | undefined;
  readonly subHeading?: ReactNode | undefined;
  readonly media?: ReactNode | undefined;
  readonly callsToAction?: ReactNode;
  readonly size?: TextBlockSize | undefined;
  readonly theme?: Theme | undefined;
}

function TwoColumn({
  description,
  eyebrow,
  heading,
  layout = 'left',
  subHeading,
  size,
  media,
  callsToAction,
  theme,
}: Props): ReactElement {
  const { rootClass, contentClassName } = useTwoColumn({
    layout,
    image: media,
  });

  const contentItems = useMemo(() => {
    const items: ReactNode[] = [];
    if (typeof media != 'undefined') {
      items.push(<div key="media">{media}</div>);
    }

    items.push(
      <TextBlock
        callsToAction={callsToAction}
        description={description}
        eyebrow={eyebrow}
        heading={heading}
        key="text"
        layout={layout}
        size={size}
        subHeading={subHeading}
        theme={theme}
      />,
    );

    return layout === 'right' ? items.reverse() : items;
  }, [
    callsToAction,
    description,
    eyebrow,
    heading,
    media,
    layout,
    size,
    subHeading,
    theme,
  ]);

  return (
    <div className={rootClass}>
      <div className={contentClassName} style={{ position: 'relative' }}>
        {contentItems}
      </div>
    </div>
  );
}

export default TwoColumn;
